import React, { useState, useEffect } from 'react';
import { getClients, saveClient, deleteClient, getInvoicesByClient, getSettings, saveInvoice, deleteInvoice, getShifts, getServiceAgreementsByClient, getRecurringScheduleByAgreement, getShiftNotesByClient, saveShiftNote, deleteShiftNote } from '../utils/storageUtils';
import RecurringShiftSetup from '../components/RecurringShiftSetup';
import { LockOpen } from 'lucide-react';
import RosterView from '../components/RosterView';
import { searchNDISItems } from '../utils/ndisUtils';
import { generateInvoicePDF } from '../utils/pdfUtils';
import { format } from 'date-fns';

function Clients() {
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [serviceSearchResults, setServiceSearchResults] = useState([]);
  const [showServiceResults, setShowServiceResults] = useState(false);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [selectedClientInvoices, setSelectedClientInvoices] = useState([]);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  const [viewingClientShifts, setViewingClientShifts] = useState([]);
  const [viewingClientInvoices, setViewingClientInvoices] = useState([]);
  const [viewingClientTab, setViewingClientTab] = useState('shifts');
  const [viewingClientInvoice, setViewingClientInvoice] = useState(null);
  const [viewingClientNotes, setViewingClientNotes] = useState([]);
  const [addingNoteForShift, setAddingNoteForShift] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [showDeleteId, setShowDeleteId] = useState(null);
  const [showRecurringSetup, setShowRecurringSetup] = useState(null);
  const [showRosterView, setShowRosterView] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    ndisNumber: '',
    dob: '',
    supportType: '',
    notes: '',
    status: 'active',
    emergencyContact: '',
    emergencyPhone: '',
    defaultServiceCode: '',
    defaultServiceDescription: '',
    hourlyRate: '',
    jobDescription: '',
    fundingType: 'self-managed', // 'self-managed' or 'plan-managed'
    // Plan Manager fields
    planManagerName: '',
    planManagerEmail: '',
    planManagerPhone: '',
    planManagerOrganization: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = () => {
    const clientsData = getClients();
    setClients(clientsData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceSearch = (query) => {
    setServiceSearchQuery(query);
    if (query.length >= 2) {
      const results = searchNDISItems(query);
      setServiceSearchResults(results.slice(0, 10));
      setShowServiceResults(true);
    } else {
      setServiceSearchResults([]);
      setShowServiceResults(false);
    }
  };

  const selectDefaultService = (item) => {
    setFormData(prev => ({
      ...prev,
      defaultServiceCode: item.item_code,
      defaultServiceDescription: item.description,
      hourlyRate: item.national_price || ''
    }));
    setServiceSearchQuery(item.description);
    setShowServiceResults(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const clientToSave = {
      ...formData,
      id: editingClient ? editingClient.id : undefined,
      updatedAt: new Date().toISOString()
    };
    
    saveClient(clientToSave);
    resetForm();
    loadClients();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ndisNumber: '',
      dob: '',
      email: '',
      phone: '',
      address: '',
      emergencyContact: '',
      emergencyPhone: '',
      defaultServiceCode: '',
      defaultServiceDescription: '',
      hourlyRate: '',
      jobDescription: '',
      notes: '',
      status: 'active',
      fundingType: 'self-managed',
      planManagerName: '',
      planManagerEmail: '',
      planManagerPhone: '',
      planManagerOrganization: ''
    });
    setServiceSearchQuery('');
    setEditingClient(null);
    setShowForm(false);
  };

  const handleEdit = (client) => {
    setFormData(client);
    setServiceSearchQuery(client.defaultServiceDescription || '');
    setEditingClient(client);
    setShowForm(true);
  };

  const handleDelete = (clientId) => {
    if (window.confirm('Are you sure you want to delete this client? This cannot be undone.')) {
      deleteClient(clientId);
      loadClients();
    }
  };

  const handleViewInvoices = (client) => {
    const invoices = getInvoicesByClient(client.id);
    setSelectedClientInvoices(invoices);
    setSelectedClientName(client.name);
    setShowInvoicesModal(true);
  };

  const handleViewClient = (client) => {
    const allShifts = getShifts();
    const clientShifts = allShifts
      .filter(shift => shift.clientId === client.id)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    const invoices = getInvoicesByClient(client.id);
    
    const clientNotes = getShiftNotesByClient(client.id);
    
    setViewingClient(client);
    setViewingClientShifts(clientShifts);
    setViewingClientInvoices(invoices);
    setViewingClientNotes(clientNotes);
    setViewingClientTab('shifts');
    setAddingNoteForShift(null);
    setNoteText('');
    setEditingNote(null);
    setShowViewModal(true);
  };

  const handleDownloadInvoice = async (invoice) => {
    const settings = getSettings();
    const pdf = await generateInvoicePDF(invoice, settings);
    pdf.save(`${invoice.invoiceNumber}_${invoice.client.name.replace(/\s+/g, '_')}.pdf`);
  };

  const handleTogglePaid = (invoice) => {
    const updatedInvoice = {
      ...invoice,
      paid: !invoice.paid,
      paidAt: !invoice.paid ? new Date().toISOString() : null
    };
    saveInvoice(updatedInvoice);
    // Reload the invoices for this client
    const invoices = getInvoicesByClient(invoice.client.id);
    setSelectedClientInvoices(invoices);
  };

  const handleViewClientInvoice = (invoice) => {
    setViewingClientInvoice(invoice);
  };

  const handleDeleteClientInvoice = (invoiceId) => {
    if (window.confirm('Are you sure you want to delete this invoice? This cannot be undone.')) {
      deleteInvoice(invoiceId);
      // Reload the invoices for this client
      const client = clients.find(c => c.name === selectedClientName);
      if (client) {
        const invoices = getInvoicesByClient(client.id);
        setSelectedClientInvoices(invoices);
      }
    }
  };

  const getClientShiftButton = (client) => {
    const sas = getServiceAgreementsByClient(client.id);
    const activeSA = sas.find(sa => sa.status && sa.status.toLowerCase() === 'active');
    if (!activeSA) return null;
    const schedule = getRecurringScheduleByAgreement(activeSA.id);
    if (schedule) {
      return (
        <button
          className="btn btn-small client-roster-btn"
          onClick={() => setShowRosterView(activeSA)}
        >
          View Roster
        </button>
      );
    }
    return (
      <button
        className="btn btn-small client-unlock-btn"
        onClick={() => setShowRecurringSetup(activeSA)}
      >
        <LockOpen size={18} color="#fff" strokeWidth={2.5} />
        <span className="unlock-text-mobile">New Feature Unlocked!</span>
        <span className="unlock-text-desktop">You've just unlocked a powerful new feature — ready to explore?</span>
      </button>
    );
  };

  const isPlanManaged = formData.fundingType === 'plan-managed';

  return (
    <div className="clients-page">
      <div className="page-header">
        <h1>Clients</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add Client'}
        </button>
      </div>

      {showForm && (
        <div className="client-form-container">
          <h2>{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
          <form onSubmit={handleSubmit} className="client-form">
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>NDIS Number *</label>
                <input
                  type="text"
                  name="ndisNumber"
                  value={formData.ndisNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="2"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Emergency Contact</label>
                <input
                  type="text"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Emergency Phone</label>
                <input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group service-search">
              <label>Default Support Item</label>
              <input
                type="text"
                value={serviceSearchQuery}
                onChange={(e) => handleServiceSearch(e.target.value)}
                placeholder="Search NDIS services... (e.g., 'community weekday')"
              />
              {showServiceResults && serviceSearchResults.length > 0 && (
                <div className="search-results">
                  {serviceSearchResults.map(item => (
                    <div 
                      key={item.item_code} 
                      className="search-result-item"
                      onClick={() => selectDefaultService(item)}
                    >
                      <div className="result-code">{item.item_code}</div>
                      <div className="result-description">{item.description}</div>
                      <div className="result-price">${item.national_price}/{item.unit}</div>
                    </div>
                  ))}
                </div>
              )}
              {showServiceResults && serviceSearchResults.length === 0 && serviceSearchQuery.length >= 2 && (
                <div className="search-results">
                  <div className="no-results">No matching services found</div>
                </div>
              )}
              <small>This will be the default service for new shifts with this client</small>
            </div>

            <div className="form-group">
              <label>Default Hourly Rate</label>
              <input
                type="number"
                name="hourlyRate"
                value={formData.hourlyRate}
                onChange={handleInputChange}
                step="0.01"
                placeholder="Auto-filled from selected service"
                readOnly={formData.defaultServiceCode ? true : false}
              />
              <small>Automatically set when you select a default support item</small>
            </div>

            <div className="form-group">
              <label>Job Description</label>
              <textarea
                name="jobDescription"
                value={formData.jobDescription}
                onChange={handleInputChange}
                rows="2"
                placeholder="e.g., Support client to access community and recreational activities"
              />
              <small>This will appear on invoices</small>
            </div>

            <div className="form-group">
              <label>Funding Type</label>
              <select
                name="fundingType"
                value={formData.fundingType}
                onChange={handleInputChange}
              >
                <option value="self-managed">Self Managed</option>
                <option value="plan-managed">Plan Managed</option>
              </select>
            </div>

            {isPlanManaged && (
              <>
                <h3>Plan Manager Details</h3>

                <div className="form-group">
                  <label>Plan Manager Name</label>
                  <input
                    type="text"
                    name="planManagerName"
                    value={formData.planManagerName}
                    onChange={handleInputChange}
                    placeholder="e.g., John Smith"
                  />
                </div>

                <div className="form-group">
                  <label>Plan Manager Organization</label>
                  <input
                    type="text"
                    name="planManagerOrganization"
                    value={formData.planManagerOrganization}
                    onChange={handleInputChange}
                    placeholder="e.g., ABC Plan Management"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Plan Manager Email</label>
                    <input
                      type="email"
                      name="planManagerEmail"
                      value={formData.planManagerEmail}
                      onChange={handleInputChange}
                      placeholder="invoices@planmanager.com"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Plan Manager Phone</label>
                    <input
                      type="tel"
                      name="planManagerPhone"
                      value={formData.planManagerPhone}
                      onChange={handleInputChange}
                      placeholder="(02) 1234 5678"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
                placeholder="Any special requirements or notes"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingClient ? 'Update Client' : 'Add Client'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="clients-list">
        {clients.length === 0 ? (
          <div className="empty-state">
            <p>No clients yet. Add your first client to get started!</p>
          </div>
        ) : (
          <div className="clients-grid">
            {clients.map(client => (
              <div key={client.id} className="client-card">
                <div className="client-header">
                  <h3>{client.name}</h3>
                  <span className={`status-badge ${client.status}`}>
                    {client.status}
                  </span>
                </div>
                <div className="client-details">
                  <p><strong>NDIS:</strong> {client.ndisNumber}</p>
                  {client.phone && <p><strong>Phone:</strong> {client.phone}</p>}
                  {client.email && <p><strong>Email:</strong> {client.email}</p>}
                  {client.jobDescription && <p><strong>Job:</strong> {client.jobDescription}</p>}
                  {client.defaultServiceDescription && (
                    <p><strong>Default Service:</strong> {client.defaultServiceDescription}</p>
                  )}
                  {client.hourlyRate && <p><strong>Rate:</strong> ${client.hourlyRate}/hour</p>}
                  
                  {client.fundingType === 'plan-managed' && (
                    client.planManagerName || 
                    client.planManagerOrganization || 
                    client.planManagerEmail || 
                    client.planManagerPhone
                  ) && (
                    <div className="client-plan-manager" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)' }}>
                      <p><strong>Plan Manager</strong></p>
                      {client.planManagerName && <p>{client.planManagerName}</p>}
                      {client.planManagerOrganization && <p>{client.planManagerOrganization}</p>}
                      {client.planManagerEmail && <p>{client.planManagerEmail}</p>}
                      {client.planManagerPhone && <p>{client.planManagerPhone}</p>}
                    </div>
                  )}
                </div>
                <div className="client-actions">
                  <button 
                    className="btn btn-small btn-success" 
                    onClick={() => handleViewClient(client)}
                  >
                    View
                  </button>
                  <button 
                    className="btn btn-small btn-secondary" 
                    onClick={() => handleEdit(client)}
                  >
                    Edit
                  </button>
                  {getClientShiftButton(client)}
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'center', position: 'relative' }}>
                    <button 
                      className="btn-dots"
                      onClick={() => setShowDeleteId(showDeleteId === client.id ? null : client.id)}
                      title="More options"
                    >
                      ⋯
                    </button>
                    {showDeleteId === client.id && (
                      <button
                        className="btn btn-small btn-danger"
                        style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '100%', marginTop: '4px', zIndex: 5, whiteSpace: 'nowrap' }}
                        onClick={() => { handleDelete(client.id); setShowDeleteId(null); }}
                      >
                        Delete Client
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invoices Modal */}
      {showInvoicesModal && (
        <div className="modal-overlay" onClick={() => setShowInvoicesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
            {/* Title and close button in header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingRight: '3rem' }}>
              <h2>Invoices for {selectedClientName}</h2>
              <button 
                className="btn-close" 
                onClick={() => setShowInvoicesModal(false)}
                style={{ position: 'absolute', top: '1.5rem', right: '1.5rem' }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {selectedClientInvoices.length === 0 ? (
                <p>No invoices found for this client.</p>
              ) : (
                <div className="shifts-table-container">
                  <table className="shifts-table">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Due Date</th>
                        <th>Paid</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedClientInvoices.map(invoice => (
                        <tr key={invoice.id}>
                          <td data-label="Invoice #"><strong>{invoice.invoiceNumber}</strong></td>
                          <td data-label="Date">{format(new Date(invoice.date), 'dd/MM/yyyy')}</td>
                          <td data-label="Amount" style={{ color: '#2ecc71', fontWeight: 'bold' }}>
                            ${invoice.total.toFixed(2)}
                          </td>
                          <td data-label="Due Date">{format(new Date(invoice.dueDate), 'dd/MM/yyyy')}</td>
                          <td data-label="Paid">
                            <input
                              type="checkbox"
                              checked={invoice.paid || false}
                              onChange={() => handleTogglePaid(invoice)}
                              style={{ 
                                width: '20px', 
                                height: '20px', 
                                cursor: 'pointer',
                                accentColor: '#6366f1'
                              }}
                            />
                          </td>
                          <td data-label="Actions">
                            <div className="action-buttons">
                              <button 
                                className="btn btn-small btn-success"
                                onClick={() => handleViewClientInvoice(invoice)}
                              >
                                View
                              </button>
                              <button 
                                className="btn btn-small btn-info"
                                onClick={() => handleDownloadInvoice(invoice)}
                              >
                                Download
                              </button>
                              <button 
                                className="btn btn-small btn-delete"
                                onClick={() => handleDeleteClientInvoice(invoice.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Client Modal */}
      {showViewModal && viewingClient && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', padding: '0', position: 'relative' }}>
            
            <div className="modal-body" style={{ padding: '0' }}>
              {/* Client Details Card - Centered */}
              <div className="client-card" style={{ 
                maxWidth: '900px', 
                margin: '3rem auto 2rem auto',
                padding: '2.5rem 3rem',
                fontSize: '1.1rem',
                position: 'relative'
              }}>
                {/* Close button in top right */}
                <button 
                  className="btn-close" 
                  onClick={() => setShowViewModal(false)}
                  style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}
                >
                  ×
                </button>
                
                <div className="client-header" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                    <span 
                      className={`status-badge ${viewingClient.status}`} 
                      style={{ 
                        fontSize: '0.7rem', 
                        padding: '0.25rem 0.7rem',
                        display: 'inline-block'
                      }}
                    >
                      {viewingClient.status.toUpperCase()}
                    </span>
                  </div>
                  <h2 style={{ fontSize: '1.8rem', margin: 0, textAlign: 'left' }}>{viewingClient.name}</h2>
                </div>
                
                <div className="client-details" style={{ fontSize: '1rem', lineHeight: '1.8' }}>
                  <p style={{ marginBottom: '0.7rem' }}><strong>NDIS:</strong> {viewingClient.ndisNumber}</p>
                  {viewingClient.dob && <p style={{ marginBottom: '0.7rem' }}><strong>Date of Birth:</strong> {format(new Date(viewingClient.dob), 'dd/MM/yyyy')}</p>}
                  {viewingClient.phone && <p style={{ marginBottom: '0.7rem' }}><strong>Phone:</strong> {viewingClient.phone}</p>}
                  {viewingClient.email && <p style={{ marginBottom: '0.7rem' }}><strong>Email:</strong> {viewingClient.email}</p>}
                  {viewingClient.address && <p style={{ marginBottom: '0.7rem' }}><strong>Address:</strong> {viewingClient.address}</p>}
                  {viewingClient.emergencyContact && <p style={{ marginBottom: '0.7rem' }}><strong>Emergency Contact:</strong> {viewingClient.emergencyContact}</p>}
                  {viewingClient.emergencyPhone && <p style={{ marginBottom: '0.7rem' }}><strong>Emergency Phone:</strong> {viewingClient.emergencyPhone}</p>}
                  {viewingClient.jobDescription && <p style={{ marginBottom: '0.7rem' }}><strong>Job:</strong> {viewingClient.jobDescription}</p>}
                  {viewingClient.defaultServiceDescription && (
                    <p style={{ marginBottom: '0.7rem' }}><strong>Default Service:</strong> {viewingClient.defaultServiceDescription}</p>
                  )}
                  {viewingClient.hourlyRate && <p style={{ marginBottom: '0.7rem' }}><strong>Rate:</strong> ${viewingClient.hourlyRate}/hour</p>}
                  
                  {viewingClient.fundingType === 'plan-managed' && (
                    viewingClient.planManagerName || 
                    viewingClient.planManagerOrganization || 
                    viewingClient.planManagerEmail || 
                    viewingClient.planManagerPhone
                  ) && (
                    <div className="client-plan-manager" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                      <p style={{ marginBottom: '0.7rem' }}><strong>Plan Manager</strong></p>
                      {viewingClient.planManagerName && <p style={{ marginBottom: '0.7rem' }}>{viewingClient.planManagerName}</p>}
                      {viewingClient.planManagerOrganization && <p style={{ marginBottom: '0.7rem' }}>{viewingClient.planManagerOrganization}</p>}
                      {viewingClient.planManagerEmail && <p style={{ marginBottom: '0.7rem' }}>{viewingClient.planManagerEmail}</p>}
                      {viewingClient.planManagerPhone && <p style={{ marginBottom: '0.7rem' }}>{viewingClient.planManagerPhone}</p>}
                    </div>
                  )}
                  
                  {viewingClient.notes && (
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                      <p style={{ marginBottom: '0.5rem' }}><strong>Notes:</strong></p>
                      <p style={{ marginBottom: '0.7rem' }}>{viewingClient.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Shifts / Invoices Tabs */}
              <div style={{ padding: '0 2rem 2rem 2rem' }}>
                <div className="client-popup-tabs">
                  <button
                    className={`client-popup-tab${viewingClientTab === 'shifts' ? ' active' : ''}`}
                    onClick={() => setViewingClientTab('shifts')}
                  >
                    Shifts ({viewingClientShifts.length})
                  </button>
                  <button
                    className={`client-popup-tab${viewingClientTab === 'invoices' ? ' active' : ''}`}
                    onClick={() => setViewingClientTab('invoices')}
                  >
                    Invoices ({viewingClientInvoices.length})
                  </button>
                  <button
                    className={`client-popup-tab${viewingClientTab === 'notes' ? ' active' : ''}`}
                    onClick={() => { setViewingClientTab('notes'); setAddingNoteForShift(null); setNoteText(''); setEditingNote(null); }}
                  >
                    Notes ({viewingClientNotes.length})
                  </button>
                </div>

                {viewingClientTab === 'shifts' && (
                  <>
                    {viewingClientShifts.length === 0 ? (
                      <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', fontSize: '1.1rem' }}>
                        No shifts recorded for this client yet.
                      </p>
                    ) : (
                      <div className="shifts-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Service</th>
                              <th>Hours</th>
                              <th>Rate</th>
                              <th>KM</th>
                              <th>Total</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewingClientShifts.map(shift => (
                              <tr key={shift.id}>
                                <td data-label="Date">{format(new Date(shift.date), 'dd/MM/yyyy')}</td>
                                <td data-label="Service">
                                  <div className="service-cell">
                                    <small>{shift.ndisItemCode}</small>
                                    <div>{shift.serviceDescription}</div>
                                  </div>
                                </td>
                                <td data-label="Hours">{shift.hours}h</td>
                                <td data-label="Rate">${shift.hourlyRate}</td>
                                <td data-label="KM">{shift.kilometers}km</td>
                                <td data-label="Total" className="amount">${shift.total?.toFixed(2)}</td>
                                <td data-label="Status">
                                  <span className={`status-badge ${shift.status}`}>
                                    {shift.status}
                                  </span>
                                </td>
                                <td data-label="Actions">
                                  <div className="action-buttons">
                                    <button 
                                      className="btn btn-small btn-info"
                                      onClick={() => {
                                        setShowViewModal(false);
                                        window.location.href = '/shifts';
                                      }}
                                      title="Go to Shifts page to edit"
                                    >
                                      Edit
                                    </button>
                                    <button 
                                      className="btn btn-small btn-danger"
                                      onClick={() => {
                                        if (window.confirm('Are you sure you want to delete this shift?')) {
                                          const { deleteShift } = require('../utils/storageUtils');
                                          deleteShift(shift.id);
                                          const allShifts = getShifts();
                                          const clientShifts = allShifts
                                            .filter(s => s.clientId === viewingClient.id)
                                            .sort((a, b) => new Date(b.date) - new Date(a.date));
                                          setViewingClientShifts(clientShifts);
                                        }
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {viewingClientTab === 'invoices' && (
                  <>
                    {viewingClientInvoices.length === 0 ? (
                      <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', fontSize: '1.1rem' }}>
                        No invoices found for this client.
                      </p>
                    ) : (
                      <div className="shifts-table client-invoices-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Invoice #</th>
                              <th>Date</th>
                              <th>Amount</th>
                              <th>Due Date</th>
                              <th>Paid</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewingClientInvoices.map(invoice => (
                              <tr key={invoice.id}>
                                <td data-label="Invoice #"><strong>{invoice.invoiceNumber}</strong></td>
                                <td data-label="Date">{format(new Date(invoice.date), 'dd/MM/yyyy')}</td>
                                <td data-label="Amount" style={{ color: '#2ecc71', fontWeight: 'bold' }}>
                                  ${invoice.total.toFixed(2)}
                                </td>
                                <td data-label="Due Date">{format(new Date(invoice.dueDate), 'dd/MM/yyyy')}</td>
                                <td data-label="Paid">
                                  <input
                                    type="checkbox"
                                    checked={invoice.paid || false}
                                    onChange={() => {
                                      handleTogglePaid(invoice);
                                      // Refresh invoices in this view
                                      setTimeout(() => {
                                        const invoices = getInvoicesByClient(viewingClient.id);
                                        setViewingClientInvoices(invoices);
                                      }, 50);
                                    }}
                                    style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#6366f1' }}
                                  />
                                </td>
                                <td data-label="Actions">
                                  <div className="action-buttons">
                                    <button 
                                      className="btn btn-small btn-success"
                                      onClick={() => handleViewClientInvoice(invoice)}
                                    >
                                      View
                                    </button>
                                    <button 
                                      className="btn btn-small btn-info"
                                      onClick={() => handleDownloadInvoice(invoice)}
                                    >
                                      Download
                                    </button>
                                    <button 
                                      className="btn btn-small btn-delete"
                                      onClick={() => {
                                        handleDeleteClientInvoice(invoice.id);
                                        setTimeout(() => {
                                          const invoices = getInvoicesByClient(viewingClient.id);
                                          setViewingClientInvoices(invoices);
                                        }, 50);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {viewingClientTab === 'notes' && (
                  <>
                    {/* Add New Shift Note */}
                    {!addingNoteForShift && !editingNote && (
                      <button
                        className="btn btn-success"
                        style={{ marginBottom: 'var(--space-lg)', width: '100%' }}
                        onClick={() => setAddingNoteForShift('selecting')}
                      >
                        + Add Shift Note
                      </button>
                    )}

                    {/* Shift selector */}
                    {addingNoteForShift === 'selecting' && (
                      <div style={{ marginBottom: 'var(--space-lg)' }}>
                        <h3 style={{ marginBottom: 'var(--space-md)', fontSize: '1rem' }}>Select a shift:</h3>
                        <button
                          className="btn btn-secondary btn-small"
                          style={{ marginBottom: 'var(--space-md)' }}
                          onClick={() => setAddingNoteForShift(null)}
                        >
                          Cancel
                        </button>
                        {viewingClientShifts.length === 0 ? (
                          <p style={{ color: 'var(--color-text-muted)' }}>No shifts recorded for this client.</p>
                        ) : (
                          <div className="shifts-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Service</th>
                                  <th>Hours</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {viewingClientShifts.map(shift => (
                                  <tr key={shift.id}>
                                    <td data-label="Date">{format(new Date(shift.date), 'dd/MM/yyyy')}</td>
                                    <td data-label="Service">{shift.serviceDescription || shift.ndisItemCode}</td>
                                    <td data-label="Hours">{shift.hours}h</td>
                                    <td data-label="Actions">
                                      <button
                                        className="btn btn-small btn-success"
                                        onClick={() => {
                                          const existingNote = viewingClientNotes.find(n => n.shiftId === shift.id);
                                          setAddingNoteForShift(shift);
                                          setNoteText(existingNote ? existingNote.text : '');
                                          setEditingNote(existingNote || null);
                                        }}
                                      >
                                        {viewingClientNotes.find(n => n.shiftId === shift.id) ? 'Edit Note' : 'Add Note'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Note editor */}
                    {addingNoteForShift && addingNoteForShift !== 'selecting' && (
                      <div style={{ marginBottom: 'var(--space-lg)' }}>
                        <h3 style={{ marginBottom: 'var(--space-sm)', fontSize: '1rem' }}>
                          Shift Note — {format(new Date(addingNoteForShift.date), 'dd/MM/yyyy')} ({addingNoteForShift.hours}h)
                        </h3>
                        <textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Write your shift notes here... (e.g., what activities were done, client mood, any incidents)"
                          style={{
                            width: '100%',
                            minHeight: '150px',
                            padding: 'var(--space-md)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            fontFamily: 'inherit',
                            fontSize: '0.95rem',
                            resize: 'vertical',
                            marginBottom: 'var(--space-md)',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                          <button
                            className="btn btn-success"
                            onClick={() => {
                              if (!noteText.trim()) return;
                              const note = {
                                ...(editingNote || {}),
                                shiftId: addingNoteForShift.id,
                                clientId: viewingClient.id,
                                shiftDate: addingNoteForShift.date,
                                text: noteText.trim(),
                              };
                              saveShiftNote(note);
                              setViewingClientNotes(getShiftNotesByClient(viewingClient.id));
                              setAddingNoteForShift(null);
                              setNoteText('');
                              setEditingNote(null);
                            }}
                          >
                            {editingNote ? 'Update Note' : 'Save Note'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => { setAddingNoteForShift(null); setNoteText(''); setEditingNote(null); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Notes list */}
                    {!addingNoteForShift && !editingNote && (
                      <>
                        {viewingClientNotes.length === 0 ? (
                          <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', fontSize: '1.1rem' }}>
                            No shift notes for this client yet.
                          </p>
                        ) : (
                          <div className="shifts-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Note</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {viewingClientNotes.map(note => (
                                  <tr key={note.id}>
                                    <td data-label="Date">{format(new Date(note.shiftDate), 'dd/MM/yyyy')}</td>
                                    <td data-label="Note" style={{ whiteSpace: 'pre-wrap', maxWidth: '400px' }}>
                                      {note.text.length > 100 ? note.text.substring(0, 100) + '...' : note.text}
                                    </td>
                                    <td data-label="Actions">
                                      <div className="action-buttons">
                                        <button
                                          className="btn btn-small btn-info"
                                          onClick={() => {
                                            const shift = viewingClientShifts.find(s => s.id === note.shiftId);
                                            if (shift) {
                                              setAddingNoteForShift(shift);
                                              setNoteText(note.text);
                                              setEditingNote(note);
                                            }
                                          }}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          className="btn btn-small btn-danger"
                                          onClick={() => {
                                            if (window.confirm('Delete this shift note?')) {
                                              deleteShiftNote(note.id);
                                              setViewingClientNotes(getShiftNotesByClient(viewingClient.id));
                                            }
                                          }}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showRecurringSetup && (
        <RecurringShiftSetup
          agreement={showRecurringSetup}
          onClose={() => setShowRecurringSetup(null)}
          onSave={(schedule) => {
            const sa = showRecurringSetup;
            setShowRecurringSetup(null);
            setShowRosterView(sa);
            loadClients();
          }}
        />
      )}

      {/* Roster View Modal */}
      {showRosterView && (
        <RosterView
          agreement={showRosterView}
          client={clients.find(c => c.id === showRosterView?.participant?.id)}
          onClose={() => setShowRosterView(null)}
          onEditSchedule={() => {
            const sa = showRosterView;
            setShowRosterView(null);
            setShowRecurringSetup(sa);
          }}
        />
      )}

      {/* View Client Invoice Modal */}
      {viewingClientInvoice && (
        <div className="modal-overlay" onClick={() => setViewingClientInvoice(null)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', padding: '0', position: 'relative' }}>
            {/* Close button in top right */}
            <button 
              className="btn-close" 
              onClick={() => setViewingClientInvoice(null)}
              style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', zIndex: 10 }}
            >
              ×
            </button>
            
            <div className="modal-body" style={{ padding: '0' }}>
              {/* Invoice content in gray box */}
              <div className="client-card" style={{ 
                margin: '3rem auto 2rem auto',
                padding: '2.5rem',
                maxWidth: '900px'
              }}>
                <div className="invoice-document">
                  <div className="invoice-header-section">
                    <div className="business-info">
                      <h3>{getSettings().businessName}</h3>
                      <p>{getSettings().address}</p>
                      <p>{getSettings().suburb} {getSettings().state} {getSettings().postcode}</p>
                      <p>ABN: {getSettings().abn}</p>
                    </div>
                  </div>

                  <h1>TAX INVOICE</h1>

                  <div className="invoice-details-section">
                    <div className="client-info">
                      {viewingClientInvoice.client.fundingType === 'plan-managed' ? (
                        <>
                          <p><strong>Attention To: {viewingClientInvoice.client.planManagerName}</strong></p>
                          {viewingClientInvoice.client.planManagerOrganization && (
                            <p>{viewingClientInvoice.client.planManagerOrganization}</p>
                          )}
                          {viewingClientInvoice.client.planManagerEmail && (
                            <p>{viewingClientInvoice.client.planManagerEmail}</p>
                          )}
                          <p style={{ marginTop: '1rem' }}><strong>Regarding Client: {viewingClientInvoice.client.name}</strong></p>
                        </>
                      ) : (
                        <>
                          <p><strong>Attention To: {viewingClientInvoice.client.name}</strong></p>
                        </>
                      )}
                      <p>NDIS: {viewingClientInvoice.client.ndisNumber}</p>
                      {viewingClientInvoice.client.dob && (
                        <p>DOB: {format(new Date(viewingClientInvoice.client.dob), 'dd/MM/yyyy')}</p>
                      )}
                      {viewingClientInvoice.client.jobDescription && (
                        <p>Job Description: {viewingClientInvoice.client.jobDescription}</p>
                      )}
                    </div>

                    <div className="invoice-meta">
                      <p>Invoice Date: {format(new Date(viewingClientInvoice.date), 'dd MMM yyyy')}</p>
                      <p>Account Number: {viewingClientInvoice.client.ndisNumber}</p>
                      <p>Invoice Number: {viewingClientInvoice.invoiceNumber}</p>
                      <p>ABN: {getSettings().abn}</p>
                    </div>
                  </div>

                  <table className="invoice-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Description</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Amount AUD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingClientInvoice.lineItems.map((item, index) => (
                        <tr key={index}>
                          <td data-label="Item">{item.itemCode}</td>
                          <td data-label="Description">{item.description}</td>
                          <td data-label="Quantity">{item.quantity.toFixed(2)}</td>
                          <td data-label="Unit Price">${item.unitPrice.toFixed(2)}</td>
                          <td data-label="Amount">${item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4" className="text-right"><strong>Subtotal</strong></td>
                        <td><strong>${viewingClientInvoice.subtotal.toFixed(2)}</strong></td>
                      </tr>
                      <tr>
                        <td colSpan="4" className="text-right"><strong>TOTAL AUD</strong></td>
                        <td><strong>${viewingClientInvoice.total.toFixed(2)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>

                  <div className="invoice-footer">
                    <p><strong>Due Date: {format(new Date(viewingClientInvoice.dueDate), 'dd MMM yyyy')}</strong></p>
                    <p>Please make payment to:</p>
                    <p>Account Name: {getSettings().bankName}</p>
                    <p>BSB: {getSettings().bsb}</p>
                    <p>Account Number: {getSettings().accountNumber}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
